import {v4 as uuidv4} from "uuid";
import store from "../../../../store";
import {getWizardGroupsFromTheWizard} from "../../../sqft/helpers/sqftHelperUi";
import {MEASURE_UNITS, PARENT_KEYS_WITH_COMBINED_FEATURES_IN_CART} from "../../../sqft/constants";

/**
 * Prepare features for SQFT cart addition and return all derived artifacts.
 *
 * @returns {{ addedFeatures: Array, cabinetFeatures: Array, previouslySelected: Array }}
 * @param parentsLists
 * @param previouslyAddedSqftFeatures
 */
export function computeAddedFeatures(parentsLists, previouslyAddedSqftFeatures) {
  const state = store.getState();
  const {wizardIsActive, wizardActiveItem} = state.wizardState;
  const fabricatorSettings = state.fabricatorState.settings;
  const {activeQuoteIndex, quotes} = state.quotesState;

  const addedFeatures = [];
  const cabinetFeatures = [];
  const newPreviouslySelectedFeatures = [...(previouslyAddedSqftFeatures || [])];


  // ---- helpers ----

  const prepareSqftFeaturesBeforeAddToCart = (sqftFeatures, parentKey, quoteIndex) => {
    let sqftFeaturesPrepared = [];

    if (PARENT_KEYS_WITH_COMBINED_FEATURES_IN_CART.includes(parentKey)) {
      sqftFeatures.forEach((feature) => {
        const alreadyAdded = sqftFeaturesPrepared.find((addedFeature) => {
          const isFoundById = addedFeature?.feature?.dbFeature?.id === feature?.feature?.dbFeature?.id;
          const isFoundByName =
            addedFeature?.feature?.getSplittedFeatureName() === feature?.feature?.getSplittedFeatureName();
          return isFoundById || isFoundByName;
        });

        if (alreadyAdded && !feature.disabled) {
          alreadyAdded.sqftIds.push(feature.sqftId);
          alreadyAdded.sqftQuantities[feature.sqftId] = feature.quantity;
          alreadyAdded.quantity += feature.quantity;
          alreadyAdded.quantityRounded += feature.quantityRounded;
          alreadyAdded.initialQuantity += feature.initialQuantity;
          if (alreadyAdded.displayFakeQuantity) {
            alreadyAdded.displayFakeQuantity += feature.displayFakeQuantity;
          }
        } else if (!feature.disabled) {
          sqftFeaturesPrepared.push({
            ...feature,
            parentKey,
            quoteIndex,
            sqftIds: [feature.sqftId],
            sqftQuantities: { [feature.sqftId]: feature.quantity },
          });
        }
      });

      sqftFeaturesPrepared.forEach((sqftFeature) => {
        sqftFeature.quantity =
          fabricatorSettings.measureUnit === MEASURE_UNITS.Metric
            ? Math.ceil10(sqftFeature.quantity, -2)
            : Math.ceil(sqftFeature.quantity);
      });
    } else {
      sqftFeaturesPrepared = sqftFeatures;
    }

    return sqftFeaturesPrepared;
  };

  const addWizardProperties = (cartItemToAdd, sqftFeature) => {
    if (!wizardIsActive || !wizardActiveItem) return cartItemToAdd;

    const enriched = { ...cartItemToAdd, fromWizardId: wizardActiveItem.id };
    if (sqftFeature.fromWizardGroupId) {
      enriched.fromWizardGroupId = sqftFeature.fromWizardGroupId;
    }

    const wizardSteps = getWizardGroupsFromTheWizard(
      sqftFeature.subPart.part.dbName || sqftFeature.subPart.part.partName,
      enriched.fromWizardGroupId,
      enriched.fromWizardGroupId ? null : sqftFeature.feature
    );

    if (wizardSteps.length) {
      const neededWizardStep =
        wizardSteps.find((wizStep) => !wizStep.stepGroups[0].hidePriceNoCart) || wizardSteps[0];
      if (!enriched.fromWizardGroupId) {
        enriched.fromWizardGroupId = neededWizardStep.stepGroups[0].id;
      }
      enriched.fromWizardStep = neededWizardStep.stepIndex;
    } else if (
      !Number.isInteger(enriched.fromWizardStep) &&
      Number.isInteger(sqftFeature.fromWizardStep)
    ) {
      enriched.fromWizardStep = sqftFeature.fromWizardStep;
    }

    return enriched;
  };

  const processSqftFeatures = (sqftFeatures, parentKey, listCtx, subGroupTitle = "") => {
    sqftFeatures.forEach((sqftFeature) => {
      if (sqftFeature.isDisabledInPopup ) {
        return;
      }
      if (sqftFeature.subPart) {
        sqftFeature.subPart.part.placeholderChanged = true;
        sqftFeature.subPart.part.firstClick[activeQuoteIndex] = true;
      }

      if (sqftFeature.feature && !sqftFeature.disabled) {
        const fromVisualizerType =
          quotes && quotes[listCtx.quoteIndex] && quotes[listCtx.quoteIndex].room
            ? quotes[listCtx.quoteIndex].room
            : document.kvConfig.visualizerType;

        const shouldSkipCeilQuantity = fabricatorSettings.measureUnit === MEASURE_UNITS.Metric;

        let cartItemToAdd = {
          selectionGroup: sqftFeature.subPart.text,
          subGroupTitle,
          currentQuote: listCtx.quoteIndex,
          fromVisualizerType,
          quantity: shouldSkipCeilQuantity
            ? Math.ceil10(sqftFeature.quantity, -2)
            : Math.ceil(sqftFeature.quantity),
          displayFakeQuantity: sqftFeature.displayFakeQuantity
            ? fabricatorSettings.measureUnit === MEASURE_UNITS.Metric
              ? Math.ceil10(sqftFeature.displayFakeQuantity, -2)
              : Math.ceil(sqftFeature.displayFakeQuantity)
            : null,
          displayFakePrice: sqftFeature.displayFakePrice,
          displayFakeFeatureGroupName: sqftFeature.displayFakeFeatureGroupName,
          partId: sqftFeature.subPart.part.partId,
          cartFeature: sqftFeature.feature,
          isBiggestFeature: sqftFeature.isBiggestFeature,
          isQuantityAddedToBiggestFeature: sqftFeature.isQuantityAddedToBiggestFeature,
          quantityGroup: sqftFeature.quantityGroup,
          unit: sqftFeature.unit,
          sqftId: sqftFeature.sqftId,
          sqftIds: sqftFeature.sqftIds,
          shouldUseRelatedParentFeatureAsMain: sqftFeature.shouldUseRelatedParentFeatureAsMain,
          sqftQuantities: sqftFeature.sqftQuantities,
          qtCatName: sqftFeature.qtCatName,
          shouldUseSlabPricing: sqftFeature.shouldUseSlabPricing,
          parentKey
        };

        cartItemToAdd = addWizardProperties(cartItemToAdd, sqftFeature);
        addedFeatures.push(cartItemToAdd);
      }

      const remember = {
        ...sqftFeature,
        currentQuote: listCtx.quoteIndex,
        selectionGroup: sqftFeature.subPart.text,
        partId: sqftFeature.subPart.part.partId,
        subPart: null,
      };

      const alreadyIdx = newPreviouslySelectedFeatures.findIndex(
        (existing) =>
          existing.sqftId === sqftFeature.sqftId && existing.currentQuote === activeQuoteIndex
      );
      if (alreadyIdx >= 0) {
        newPreviouslySelectedFeatures.splice(alreadyIdx, 1, remember);
      } else {
        newPreviouslySelectedFeatures.push(remember);
      }
    });
  };

  // iterate
  (parentsLists || []).forEach((parentsList) => {
    Object.keys(parentsList || {}).forEach((parentKey) => {
      const parent = parentsList[parentKey];
      if (!parent) return;

      if (parent.features?.length) {
        const prepared = prepareSqftFeaturesBeforeAddToCart(
          parent.features,
          parentKey,
          parentsList.quoteIndex
        );
        processSqftFeatures(prepared, parentKey, parentsList);
      }

      if (parent.featuresSubGroups?.length) {
        for (const featureGroup of parent.featuresSubGroups) {
          if (featureGroup?.features?.length) {
            const prepared = prepareSqftFeaturesBeforeAddToCart(
              featureGroup.features,
              parentKey,
              parentsList.quoteIndex
            );
            processSqftFeatures(prepared, parentKey, parentsList, featureGroup.subGroupTitle || "");
          }
        }
      }
    });
  });

  return {
    addedFeatures,
    cabinetFeatures,
    previouslySelected: newPreviouslySelectedFeatures
  };
}

export const addUUIDToCartItems = (cartItems) => {
  return cartItems.map(cItem => { return {...cItem, id: uuidv4()}})
}
